import { createContext, useContext, useEffect, useMemo, useState } from "react";

// Texts of the home page, in the two languages of the site. The app itself speaks 11 languages (see LANGUAGES below).
const TEXT = {
  it: {
    navHow: "Come funziona",
    navTours: "I tour",
    navFeatures: "Funzioni",
    navDownload: "Scarica",
    eyebrow: "Capri · Costiera Amalfitana",
    tagline: "La tua guida personale in tasca",
    lead: "Audioguide per il tuo tour in barca e per quello a terra. Mappa, distanza e tempo di arrivo ai punti, in 11 lingue.",
    free: "Gratuita",
    freeNote: "Le audioguide richiedono un account gratuito",
    howLabel: "Come funziona",
    howTitle: "Tre passi per scoprire Capri e la Costiera",
    steps: [
      { title: "Scarica l'app", text: "GozzoGuide è gratuita su App Store e Google Play." },
      { title: "Scegli il tuo tour", text: "In barca o a terra: l'app mostra subito i punti sulla mappa." },
      { title: "Ascolta e scopri", text: "Ogni punto ha la sua audioguida, nella tua lingua, al tuo ritmo." },
    ],
    toursLabel: "I due tour",
    toursTitle: "Dal mare alle piazzette",
    sea: {
      tag: "Tour in barca",
      title: "Capri e Costiera Amalfitana dal mare",
      text: "I punti da non perdere lungo il percorso, con la distanza in miglia nautiche e il tempo di arrivo stimato, calcolati dalla tua posizione mentre navighi.",
      items: ["Faraglioni, grotte, Positano, Amalfi", "Distanza e tempo di arrivo ai punti", "Porti di imbarco"],
    },
    land: {
      tag: "Tour a terra",
      title: "Quanto tempo hai? Ti proponiamo i punti giusti",
      text: "Dici quante ore hai a terra e da quale porto parti: l'app propone i punti da visitare e ti avvisa 30 minuti prima dell'orario di rientro alla barca.",
      items: ["Da 1 a 4 ore a terra", "Avviso di rientro", "Distanze e tempi a piedi, lungo i percorsi"],
    },
    featuresLabel: "Funzioni",
    featuresTitle: "Tutto quello che serve, niente di più",
    features: [
      { title: "Audioguide narrate", text: "Racconti curati su storia, natura e curiosità di ogni punto." },
      { title: "In 11 lingue", text: "La lingua del telefono viene riconosciuta da sola." },
      { title: "Avvisi di vicinanza", text: "Facoltativi: una notifica quando sei vicino a un punto, anche con l'app chiusa." },
      { title: "Anche senza rete", text: "Scarica audio e foto del tour prima di partire." },
    ],
    screensLabel: "L'app",
    screensTitle: "Pensata per usarla in barca e a piedi",
    shots: ["Trova il tuo tour", "Il tuo tour", "Mappa in barca", "Mappa a terra", "Scheda del punto", "Porti di imbarco", "Info"],
    langsLabel: "Lingue",
    langsTitle: "Parla la lingua dei tuoi ospiti",
    downloadLabel: "Scarica",
    downloadTitle: "Porta GozzoGuide con te",
    downloadText: "Disponibile su iPhone e Android.",
    contactLabel: "Contatti",
    contactTitle: "Domande, idee, collaborazioni?",
    contactText: "Scrivici: ci fa piacere sentirti.",
    follow: "Seguici",
    footerSite: "Sito",
    footerApp: "App",
    privacy: "Privacy",
    terms: "Termini",
    cookies: "Cookie",
    appPrivacy: "Privacy dell'app",
    appTerms: "Termini dell'app",
    appCookies: "Cookie dell'app",
    deleteAccount: "Elimina account",
    rights: "Tutti i diritti riservati.",
    menu: "Apri il menu",
    skip: "Vai al contenuto",
    downloadOn: "Scarica da",
    getItOn: "Disponibile su",
  },
  en: {
    navHow: "How it works",
    navTours: "The tours",
    navFeatures: "Features",
    navDownload: "Download",
    eyebrow: "Capri · Amalfi Coast",
    tagline: "Your personal guide in your pocket",
    lead: "Audio guides for your boat tour and your tour on land. Map, distance and arrival time to every point, in 11 languages.",
    free: "Free",
    freeNote: "The audio guides need a free account",
    howLabel: "How it works",
    howTitle: "Three steps to discover Capri and the Coast",
    steps: [
      { title: "Get the app", text: "GozzoGuide is free on the App Store and Google Play." },
      { title: "Choose your tour", text: "By boat or on land: the app shows the points on the map at once." },
      { title: "Listen and discover", text: "Every point has its audio guide, in your language, at your pace." },
    ],
    toursLabel: "The two tours",
    toursTitle: "From the sea to the little squares",
    sea: {
      tag: "Boat tour",
      title: "Capri and the Amalfi Coast from the sea",
      text: "The points not to miss along the route, with the distance in nautical miles and the estimated arrival time, worked out from your position while you sail.",
      items: ["Faraglioni, grottoes, Positano, Amalfi", "Distance and arrival time to the points", "Embarkation ports"],
    },
    land: {
      tag: "Land tour",
      title: "How much time do you have? We suggest the right points",
      text: "Tell the app how many hours you have ashore and the port you leave from: it suggests the points to visit and reminds you 30 minutes before you must be back at the boat.",
      items: ["From 1 to 4 hours ashore", "Return reminder", "Walking distances and times, along the paths"],
    },
    featuresLabel: "Features",
    featuresTitle: "Everything you need, nothing more",
    features: [
      { title: "Narrated audio guides", text: "Carefully written stories about the history, nature and curiosities of every point." },
      { title: "In 11 languages", text: "The language of your phone is recognised by itself." },
      { title: "Proximity alerts", text: "Optional: a notification when you are near a point, even with the app closed." },
      { title: "Even without a signal", text: "Download the audio and photos of the tour before you leave." },
    ],
    screensLabel: "The app",
    screensTitle: "Made to be used on a boat and on foot",
    shots: ["Find your tour", "Your tour", "Sea map", "Land map", "Point card", "Boarding ports", "Info"],
    langsLabel: "Languages",
    langsTitle: "Speaks the language of your guests",
    downloadLabel: "Download",
    downloadTitle: "Take GozzoGuide with you",
    downloadText: "Available on iPhone and Android.",
    contactLabel: "Contact",
    contactTitle: "Questions, ideas, partnerships?",
    contactText: "Write to us: we would love to hear from you.",
    follow: "Follow us",
    footerSite: "Website",
    footerApp: "App",
    privacy: "Privacy",
    terms: "Terms",
    cookies: "Cookies",
    appPrivacy: "App privacy",
    appTerms: "App terms",
    appCookies: "App cookies",
    deleteAccount: "Delete account",
    rights: "All rights reserved.",
    menu: "Open the menu",
    skip: "Skip to the content",
    downloadOn: "Download on the",
    getItOn: "Get it on",
  },
};

// The languages of the app, written in their own language
export const LANGUAGES = ["Italiano", "English", "Español", "Français", "Deutsch", "Português", "Русский", "日本語", "中文", "हिंदी", "العربية"];

const STORAGE_KEY = "gozzoguide_site_lang";
const SiteLangContext = createContext({ lang: "it", setLang: () => {}, t: TEXT.it });

const initialLang = () => {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === "it" || saved === "en") return saved;
  } catch {
    // storage blocked: the browser language decides
  }
  return String(navigator.language || "it").toLowerCase().startsWith("it") ? "it" : "en";
};

export function SiteLangProvider({ children }) {
  const [lang, setLangState] = useState(initialLang);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo(() => {
    const setLang = (next) => {
      setLangState(next);
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // storage blocked: the choice only lasts for this visit
      }
    };
    return { lang, setLang, t: TEXT[lang] };
  }, [lang]);

  return <SiteLangContext.Provider value={value}>{children}</SiteLangContext.Provider>;
}

export const useSiteLang = () => useContext(SiteLangContext);
